// =============================================================================
// ImportInvestmentsDialog.js — Phase 65.
//
// Universal investment-statement importer. ONE Dialog, all states.
//
// Flow:
//   idle      → user picks a PDF (auto-detects encryption → maybe prompt PW)
//   parsing   → /api/import/investments/parse (synchronous Gemini call)
//   review    → editable table of parsed holdings
//   committing→ /api/import/investments/jobs/{id}/commit
//   done      → success state + auto-close + parent refetch
//
// Why a single component (not multiple modals):
//   • Keeps Wealth.js untouched apart from one button + onSuccess hook.
//   • All transient state (file, job, edits) lives here so closing the dialog
//     in the middle is a no-op rollback (we POST /discard on close).
//   • The review tray is the only screen the user lingers on — every other
//     state is a transient "loading / locked / done" shell.
// =============================================================================
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Upload, Loader2, FileText, Sparkles, CheckCircle2, AlertCircle, Info,
  TrendingUp, Landmark, Coins, Wallet, Lock,
} from 'lucide-react';
import LockedPdfDialog from '@/components/LockedPdfDialog';
import { isPdfEncrypted } from '@/lib/pdfEncryption';
import { parseInvestmentCsv, hasImportableContent } from '@/lib/csvInvestmentParser';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { formatMoney, CURRENCIES } from '@/lib/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_SIZE_MB = 15;

// Mirrors KINDS from Wealth.js — kept local to avoid an export dance.
const KIND_OPTIONS = [
  { value: 'mutual_fund', label: 'Mutual Fund', Icon: TrendingUp },
  { value: 'stock',       label: 'Stock',       Icon: TrendingUp },
  { value: 'bond',        label: 'Bond',        Icon: FileText   },
  { value: 'gold',        label: 'Gold',        Icon: Sparkles   },
  { value: 'deposit',     label: 'Deposit',     Icon: Landmark   },
  { value: 'other',       label: 'Other',       Icon: Wallet     },
];
const KIND_LABEL = Object.fromEntries(KIND_OPTIONS.map((k) => [k.value, k.label]));

const FORMAT_LABEL = {
  cams_cas:       'CAMS CAS',
  kfintech_cas:   'KFintech CAS',
  nsdl_cas:       'NSDL CAS',
  cdsl_demat:     'CDSL Demat',
  nsdl_demat:     'NSDL Demat',
  broker_generic: 'Broker statement',
  unknown:        'Unrecognized format',
};

const QUALITY_BADGE = {
  high:   { label: 'High confidence', tone: 'text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/30' },
  medium: { label: 'Mixed confidence', tone: 'text-amber-700 dark:text-amber-400 bg-amber-500/10 border-amber-500/30' },
  low:    { label: 'Low confidence',   tone: 'text-rose-700 dark:text-rose-400 bg-rose-500/10 border-rose-500/30' },
};

async function fileToBase64(file) {
  const buf = await file.arrayBuffer();
  // Chunked encode to avoid call-stack overflow on large files.
  const bytes = new Uint8Array(buf);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export default function ImportInvestmentsDialog({ open, onOpenChange, onCommitted }) {
  const { user } = useAuth();
  const [phase, setPhase] = useState('idle'); // idle | parsing | review | committing | done
  const [file, setFile] = useState(null);
  const [job, setJob] = useState(null);     // { job_id, detected_format, ... }
  const [items, setItems] = useState([]);   // editable copy of items
  const [edits, setEdits] = useState({});   // { itemId: { kind?, name?, units?, value?, currency?, merge_into_holding_id? } }
  const [selected, setSelected] = useState({}); // { itemId: bool }
  const [lockedPdf, setLockedPdf] = useState(null); // { file, errorMessage, busy }
  const [committed, setCommitted] = useState(null); // { inserted, merged, skipped, activity_inserted }
  // Phase 65.1a — CSV path only. Client-parsed activity rows that will be
  // committed as transactions alongside the holdings, plus a stats block
  // for the review UI banner ("18 dividends will be imported, 245 buys
  // skipped…").
  const [csvActivity, setCsvActivity] = useState([]);
  const [csvActivityStats, setCsvActivityStats] = useState(null);
  const [importActivity, setImportActivity] = useState(true); // user toggle
  const fileInputRef = useRef(null);
  const closingForCommitRef = useRef(false);

  // ---- Reset everything when the dialog is closed (clean re-open) -----------
  const fullReset = useCallback(() => {
    setPhase('idle');
    setFile(null);
    setJob(null);
    setItems([]);
    setEdits({});
    setSelected({});
    setLockedPdf(null);
    setCommitted(null);
    setCsvActivity([]);
    setCsvActivityStats(null);
    setImportActivity(true);
    closingForCommitRef.current = false;
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  // ---- POST /discard if the user closes mid-review --------------------------
  // We don't await — best-effort. The job row exists; leaving it 'parsed' is
  // harmless but worse hygiene (it would show in any future "pending imports"
  // surface).
  const tryDiscard = useCallback(async (jobId) => {
    if (!jobId) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) return;
      await axios.post(
        `${BACKEND_URL}/api/import/investments/jobs/${jobId}/discard`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch {
      // best-effort
    }
  }, []);

  const handleOpenChange = useCallback((next) => {
    if (!next) {
      // closing
      if (phase === 'review' && job?.job_id && !closingForCommitRef.current) {
        // Fire-and-forget discard; don't block the close.
        tryDiscard(job.job_id);
      }
      fullReset();
    }
    onOpenChange?.(next);
  }, [phase, job, fullReset, tryDiscard, onOpenChange]);

  // ---- File picker ----------------------------------------------------------
  const onPickFile = async (f) => {
    if (!f) return;
    const isPdf = /\.pdf$/i.test(f.name);
    const isCsv = /\.csv$/i.test(f.name);
    if (!isPdf && !isCsv) {
      toast.error('Please pick a PDF or CSV investment statement.');
      return;
    }
    if (f.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(`File too large. Max ${MAX_SIZE_MB} MB.`);
      return;
    }
    setFile(f);
    if (isCsv) {
      await sendParseCsv(f);
      return;
    }
    // PDF path (unchanged) — detect encryption first, then send to backend.
    try {
      const enc = await isPdfEncrypted(f);
      if (enc) {
        setLockedPdf({ file: f, errorMessage: null, busy: false });
        return;
      }
    } catch {
      // Detection failure isn't fatal — backend will tell us via 401.
    }
    await sendParse(f, null);
  };

  // ---- Phase 65.1a — CSV parse handler --------------------------------------
  // Client-side extraction (no LLM cost, instant), then POST to
  // /api/import/investments/parse-csv which validates and creates the job
  // record. Activity rows round-trip via the client until commit.
  const sendParseCsv = async (f) => {
    setPhase('parsing');
    try {
      const text = await f.text();
      const parsed = parseInvestmentCsv(text, { filename: f.name });
      if (!hasImportableContent(parsed)) {
        toast.error(parsed.warnings[0] || 'We couldn’t find any holdings or importable activity in this CSV.');
        setPhase('idle');
        return;
      }
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        toast.error('You appear to be signed out. Please sign in again.');
        setPhase('idle');
        return;
      }
      const { data } = await axios.post(
        `${BACKEND_URL}/api/import/investments/parse-csv`,
        {
          filename: f.name,
          detected_format: parsed.detected_format,
          detected_currency: parsed.detected_currency,
          statement_as_of: parsed.statement_as_of,
          holdings: parsed.holdings,
          activity: parsed.activity,
          activity_stats: parsed.activity_stats,
          warnings: parsed.warnings,
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 },
      );
      setJob(data);
      const rawItems = data.items || [];
      setItems(rawItems);
      const initSel = {};
      rawItems.forEach((it) => { initSel[it.id] = true; });
      setSelected(initSel);
      setEdits({});
      setCsvActivity(data.activity || []);
      setCsvActivityStats(data.activity_stats || null);
      setPhase('review');
      if (rawItems.length === 0 && (data.activity || []).length === 0) {
        toast.info('This CSV had no holdings or importable activity.');
      } else if (data.warning) {
        toast(data.warning, { duration: 8000 });
      }
    } catch (err) {
      console.error('inv-parse-csv error', err);
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 503 && typeof detail === 'string' && detail.includes('schema_phase65')) {
        toast.error('Database not yet migrated for Phase 65. An admin needs to run schema_phase65_investment_imports.sql.', { duration: 10000 });
        setPhase('idle');
        return;
      }
      const msg = (typeof detail === 'string' && detail) || err?.message || 'We couldn’t parse this CSV. Please try a different file.';
      toast.error(msg, { duration: 8000 });
      setPhase('idle');
    }
  };

  // ---- Submit to backend ----------------------------------------------------
  const sendParse = async (f, pdfPassword) => {
    setPhase('parsing');
    try {
      const file_base64 = await fileToBase64(f);
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        toast.error('You appear to be signed out. Please sign in again.');
        setPhase('idle');
        return;
      }
      const { data } = await axios.post(
        `${BACKEND_URL}/api/import/investments/parse`,
        { file_base64, filename: f.name, pdf_password: pdfPassword || null },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 },
      );
      // Server responded — clear the locked dialog if it was open.
      setLockedPdf(null);
      setJob(data);
      const rawItems = data.items || [];
      setItems(rawItems);
      const initSel = {};
      rawItems.forEach((it) => { initSel[it.id] = true; });
      setSelected(initSel);
      setEdits({});
      setPhase('review');
      if (rawItems.length === 0) {
        toast.info(data.warning || 'No holdings found in this PDF.');
      } else if (data.warning) {
        toast(data.warning, { duration: 8000 });
      }
    } catch (err) {
      console.error('inv-parse error', err);
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const errCode = detail && typeof detail === 'object' ? detail.error : null;
      const errMsg  = detail && typeof detail === 'object' ? detail.message : null;
      // Phase 42 — locked PDF / wrong password
      if (status === 401 && (errCode === 'pdf_password_required' || errCode === 'pdf_password_invalid')) {
        setLockedPdf({
          file: f,
          errorMessage: errCode === 'pdf_password_invalid' ? (errMsg || 'That password didn’t work. Try again.') : null,
          busy: false,
        });
        setPhase('idle');
        return;
      }
      // Schema not migrated yet
      if (status === 503 && typeof detail === 'string' && detail.includes('schema_phase65')) {
        toast.error('Database not yet migrated for Phase 65. An admin needs to run schema_phase65_investment_imports.sql.', { duration: 10000 });
        setPhase('idle');
        return;
      }
      const msg = (typeof detail === 'string' && detail) || err?.message || 'We couldn’t parse this PDF. Please try a different file.';
      toast.error(msg, { duration: 8000 });
      setPhase('idle');
    }
  };

  // ---- LockedPdfDialog submit handler ---------------------------------------
  const submitLockedPdfPassword = async (password) => {
    if (!lockedPdf) return;
    setLockedPdf((p) => p ? { ...p, busy: true, errorMessage: null } : p);
    await sendParse(lockedPdf.file, password);
    setLockedPdf((p) => p ? { ...p, busy: false } : p);
  };

  // ---- Edit helpers ---------------------------------------------------------
  const setEdit = (itemId, patch) => {
    setEdits((prev) => ({ ...prev, [itemId]: { ...(prev[itemId] || {}), ...patch } }));
  };
  const getField = (item, key) => {
    const e = edits[item.id] || {};
    return e[key] !== undefined ? e[key] : item[key];
  };

  // ---- Commit ---------------------------------------------------------------
  const commit = async () => {
    if (!job?.job_id) return;
    const payload = items.map((it) => {
      const e = edits[it.id] || {};
      return {
        id: it.id,
        selected: !!selected[it.id],
        kind: e.kind ?? null,
        name: e.name ?? null,
        symbol: e.symbol ?? null,
        units: e.units ?? null,
        value: e.value ?? null,
        cost_basis: e.cost_basis ?? null,
        currency: e.currency ?? null,
        account_hint: e.account_hint ?? null,
        merge_into_holding_id: e.merge_into_holding_id ?? null,
      };
    });
    const nSelected = payload.filter((p) => p.selected).length;
    const willImportActivity = importActivity && csvActivity && csvActivity.length > 0;
    if (nSelected === 0 && !willImportActivity) {
      toast.info('Pick at least one holding (or enable activity import) to continue.');
      return;
    }
    setPhase('committing');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      // Phase 65.1a — attach activity rows when the user opted in and the
      // source was a CSV. Backend inserts them as transactions after the
      // holdings commit succeeds. Failures here don't roll back holdings.
      const body = { items: payload };
      if (importActivity && csvActivity && csvActivity.length > 0) {
        body.activity_rows = csvActivity;
      }
      const { data } = await axios.post(
        `${BACKEND_URL}/api/import/investments/jobs/${job.job_id}/commit`,
        body,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 },
      );
      setCommitted(data);
      setPhase('done');
      // Tell the parent to refetch its holdings list.
      closingForCommitRef.current = true;
      try { onCommitted?.(); } catch { /* ignore parent errors */ }
      const niceCount = (data.inserted || 0) + (data.merged || 0);
      const actCount = data.activity_inserted || 0;
      let msg = `Imported ${niceCount} holding${niceCount === 1 ? '' : 's'}`;
      if (data.merged) msg += ` (${data.merged} merged)`;
      if (actCount > 0) msg += ` · ${actCount} activity row${actCount === 1 ? '' : 's'}`;
      toast.success(`${msg}.`, { duration: 5000 });
      // Auto-close after a beat so the user sees the success state.
      setTimeout(() => { handleOpenChange(false); }, 1800);
    } catch (err) {
      console.error('inv-commit error', err);
      const detail = err?.response?.data?.detail;
      const msg = (typeof detail === 'string' && detail) || err?.message || 'Could not save these holdings.';
      toast.error(msg, { duration: 8000 });
      setPhase('review');
    }
  };

  // ---- Derived ---------------------------------------------------------------
  const selectedCount = useMemo(
    () => Object.values(selected).filter(Boolean).length,
    [selected],
  );
  const mergeCount = useMemo(
    () => items.filter((it) => selected[it.id] && (edits[it.id]?.merge_into_holding_id ?? it.match_candidate_id)).length,
    [items, selected, edits],
  );

  // ===========================================================================
  // RENDER
  // ===========================================================================
  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className="sm:max-w-4xl rounded-2xl max-h-[90vh] overflow-hidden flex flex-col"
          data-testid="import-investments-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Import investments
            </DialogTitle>
            <DialogDescription>
              Upload any investment statement — PDF or CSV. CAMS CAS, KFintech,
              NSDL, CDSL, Vanguard, Schwab, Fidelity, IBKR, and more. We&rsquo;ll
              extract your holdings; you review &amp; confirm.
            </DialogDescription>
          </DialogHeader>

          {/* ---- IDLE: file picker ---------------------------------------- */}
          {phase === 'idle' && (
            <div className="space-y-4">
              <label
                htmlFor="inv-import-file"
                className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-border/60 hover:border-primary/40 hover:bg-muted/30 transition-colors cursor-pointer py-12 text-center"
                data-testid="inv-import-dropzone"
              >
                <Upload className="h-8 w-8 text-muted-foreground mb-3" />
                <span className="font-medium">Drop a PDF or CSV here, or click to choose</span>
                <span className="text-xs text-muted-foreground mt-1">Max {MAX_SIZE_MB} MB · Password-protected PDFs supported</span>
                <input
                  id="inv-import-file"
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,.pdf,text/csv,.csv"
                  className="hidden"
                  onChange={(e) => onPickFile(e.target.files?.[0])}
                  data-testid="inv-import-file-input"
                />
              </label>
              <div className="rounded-xl bg-muted/40 border border-border/60 p-3 text-xs text-muted-foreground flex gap-2">
                <Info className="h-4 w-4 shrink-0 mt-0.5" />
                <p>
                  Files are processed for extraction only and never stored. Passwords are
                  held in memory for the parse step and then discarded.
                </p>
              </div>
            </div>
          )}

          {/* ---- PARSING ---------------------------------------------------- */}
          {phase === 'parsing' && (
            <div className="py-12 flex flex-col items-center text-center" data-testid="inv-import-parsing">
              <Loader2 className="h-8 w-8 text-primary animate-spin mb-4" />
              <div className="font-medium">Reading your statement&hellip;</div>
              <div className="text-sm text-muted-foreground mt-1">
                {file?.name ? `Parsing ${file.name}` : 'This usually takes 10–30 seconds.'}
              </div>
              <div className="text-xs text-muted-foreground mt-3 max-w-md">
                We&rsquo;re using AI to detect the format and extract every holding. CAS and demat
                statements are typically faster than full broker statements.
              </div>
            </div>
          )}

          {/* ---- REVIEW ----------------------------------------------------- */}
          {phase === 'review' && job && (
            <div className="flex-1 overflow-hidden flex flex-col gap-3 min-h-0">
              {/* Job summary banner */}
              <div className="rounded-xl border border-border/60 bg-muted/30 px-3 py-2 flex flex-wrap items-center gap-2 text-sm">
                <Badge variant="outline" className="font-normal" data-testid="inv-import-format-badge">
                  {FORMAT_LABEL[job.detected_format] || job.detected_format}
                </Badge>
                <Badge
                  variant="outline"
                  className={`font-normal ${QUALITY_BADGE[job.parse_quality]?.tone || ''}`}
                  data-testid="inv-import-quality-badge"
                >
                  {QUALITY_BADGE[job.parse_quality]?.label || job.parse_quality}
                </Badge>
                {job.statement_as_of && (
                  <span className="text-xs text-muted-foreground">As of {job.statement_as_of}</span>
                )}
                <span className="ml-auto text-xs text-muted-foreground">
                  {items.length} holding{items.length === 1 ? '' : 's'} found
                </span>
              </div>

              {job.warning && (
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-sm flex gap-2 items-start">
                  <AlertCircle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                  <span>{job.warning}</span>
                </div>
              )}

              {/* Phase 65.1a — CSV activity summary + opt-in */}
              {csvActivityStats && (
                <div className="rounded-xl border border-primary/25 bg-primary/[0.04] px-3 py-2.5 space-y-2" data-testid="inv-import-activity-card">
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="import-activity-toggle"
                      checked={importActivity && csvActivity.length > 0}
                      disabled={csvActivity.length === 0}
                      onCheckedChange={(v) => setImportActivity(!!v)}
                      className="mt-1"
                      data-testid="inv-import-activity-toggle"
                    />
                    <div className="flex-1 min-w-0">
                      <label htmlFor="import-activity-toggle" className="text-sm font-medium cursor-pointer">
                        {csvActivity.length > 0
                          ? `Also import ${csvActivity.length} activity row${csvActivity.length === 1 ? '' : 's'} as transactions`
                          : 'This file has no cash-flow activity to import'}
                      </label>
                      <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        {csvActivity.length > 0 && (
                          <>
                            <span className="text-emerald-700 dark:text-emerald-400">
                              {csvActivityStats.dividends > 0 && `${csvActivityStats.dividends} dividend${csvActivityStats.dividends === 1 ? '' : 's'}`}
                              {csvActivityStats.interest > 0 && ` · ${csvActivityStats.interest} interest`}
                              {csvActivityStats.capital_gains > 0 && ` · ${csvActivityStats.capital_gains} capital gain${csvActivityStats.capital_gains === 1 ? '' : 's'}`}
                              {csvActivityStats.fees > 0 && ` · ${csvActivityStats.fees} fee${csvActivityStats.fees === 1 ? '' : 's'}`}
                            </span>
                            {(csvActivityStats.skipped_internal > 0 || csvActivityStats.skipped_transfer > 0) && (
                              <span>
                                {' '}· Skipped {(csvActivityStats.skipped_internal + csvActivityStats.skipped_transfer)} internal move
                                {(csvActivityStats.skipped_internal + csvActivityStats.skipped_transfer) === 1 ? '' : 's'} (buys / sells / transfers) — these update holdings, not cash flow.
                              </span>
                            )}
                            {csvActivityStats.unknown > 0 && (
                              <span>{' '}· {csvActivityStats.unknown} unclassified row{csvActivityStats.unknown === 1 ? '' : 's'} skipped.</span>
                            )}
                          </>
                        )}
                        {csvActivity.length === 0 && csvActivityStats.total_rows > 0 && (
                          <>
                            The transactions section contained {csvActivityStats.total_rows} row
                            {csvActivityStats.total_rows === 1 ? '' : 's'} but they were all internal moves
                            (buys / sells / reinvestments) — no cash-flow impact.
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {items.length === 0 ? (
                <div className="rounded-xl border border-border/60 bg-muted/20 px-4 py-8 text-center text-sm text-muted-foreground">
                  {csvActivity.length > 0
                    ? 'No holdings section detected — but activity rows above are ready to import.'
                    : 'No holdings to import from this file.'}
                </div>
              ) : (
                <div className="flex-1 overflow-auto rounded-xl border border-border/60">
                  <table className="w-full text-sm" data-testid="inv-import-review-table">
                    <thead className="sticky top-0 bg-background/95 backdrop-blur border-b border-border/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="p-2 w-10"></th>
                        <th className="p-2">Holding</th>
                        <th className="p-2 w-32">Type</th>
                        <th className="p-2 w-28 text-right">Units</th>
                        <th className="p-2 w-32 text-right">Value</th>
                        <th className="p-2 w-20">Ccy</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/40">
                      {items.map((it) => {
                        const isOn = !!selected[it.id];
                        const mergeId = edits[it.id]?.merge_into_holding_id ?? it.match_candidate_id;
                        const name = getField(it, 'name');
                        const kind = getField(it, 'kind');
                        const units = getField(it, 'units');
                        const value = getField(it, 'value');
                        const currency = getField(it, 'currency');
                        return (
                          <tr key={it.id} className={`${isOn ? '' : 'opacity-50'}`} data-testid={`inv-import-row-${it.id}`}>
                            <td className="p-2 align-top">
                              <Checkbox
                                checked={isOn}
                                onCheckedChange={(v) => setSelected((s) => ({ ...s, [it.id]: !!v }))}
                                data-testid={`inv-import-row-toggle-${it.id}`}
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                value={name || ''}
                                onChange={(e) => setEdit(it.id, { name: e.target.value })}
                                className="h-8 text-sm"
                                data-testid={`inv-import-row-name-${it.id}`}
                              />
                              <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                                {it.account_hint && <span className="truncate max-w-[260px]">{it.account_hint}</span>}
                                {mergeId && (
                                  <Badge variant="outline" className="text-[10px] py-0 h-4 border-emerald-500/40 text-emerald-700 dark:text-emerald-400 bg-emerald-500/10">
                                    Will merge with existing
                                  </Badge>
                                )}
                                {it.confidence != null && it.confidence < 0.6 && (
                                  <span className="text-amber-700 dark:text-amber-400">low confidence</span>
                                )}
                              </div>
                              {mergeId && (
                                <button
                                  type="button"
                                  className="mt-1 text-[11px] text-primary hover:underline"
                                  onClick={() => setEdit(it.id, { merge_into_holding_id: null })}
                                  data-testid={`inv-import-row-create-new-${it.id}`}
                                >
                                  Create new instead
                                </button>
                              )}
                            </td>
                            <td className="p-2 align-top">
                              <Select
                                value={kind}
                                onValueChange={(v) => setEdit(it.id, { kind: v })}
                              >
                                <SelectTrigger className="h-8 text-sm" data-testid={`inv-import-row-kind-${it.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {KIND_OPTIONS.map((k) => (
                                    <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="p-2 align-top">
                              <Input
                                type="number"
                                step="any"
                                value={units == null ? '' : units}
                                onChange={(e) => setEdit(it.id, { units: e.target.value === '' ? null : Number(e.target.value) })}
                                className="h-8 text-sm text-right tabular-nums"
                                data-testid={`inv-import-row-units-${it.id}`}
                              />
                            </td>
                            <td className="p-2 align-top">
                              <Input
                                type="number"
                                step="any"
                                value={value == null ? '' : value}
                                onChange={(e) => setEdit(it.id, { value: e.target.value === '' ? null : Number(e.target.value) })}
                                className="h-8 text-sm text-right tabular-nums"
                                data-testid={`inv-import-row-value-${it.id}`}
                              />
                            </td>
                            <td className="p-2 align-top">
                              <Select
                                value={currency}
                                onValueChange={(v) => setEdit(it.id, { currency: v })}
                              >
                                <SelectTrigger className="h-8 text-sm" data-testid={`inv-import-row-currency-${it.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {(CURRENCIES || []).map((c) => (
                                    <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>
                                  ))}
                                  {!CURRENCIES && (
                                    <>
                                      <SelectItem value="INR">INR</SelectItem>
                                      <SelectItem value="USD">USD</SelectItem>
                                      <SelectItem value="GBP">GBP</SelectItem>
                                      <SelectItem value="EUR">EUR</SelectItem>
                                    </>
                                  )}
                                </SelectContent>
                              </Select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ---- COMMITTING ------------------------------------------------- */}
          {phase === 'committing' && (
            <div className="py-12 flex flex-col items-center text-center" data-testid="inv-import-committing">
              <Loader2 className="h-8 w-8 text-primary animate-spin mb-4" />
              <div className="font-medium">Saving your holdings&hellip;</div>
            </div>
          )}

          {/* ---- DONE ------------------------------------------------------- */}
          {phase === 'done' && committed && (
            <div className="py-10 flex flex-col items-center text-center" data-testid="inv-import-done">
              <CheckCircle2 className="h-10 w-10 text-emerald-600 dark:text-emerald-400 mb-4" />
              <div className="font-display font-semibold text-lg">All set!</div>
              <div className="text-sm text-muted-foreground mt-1">
                Imported {committed.inserted || 0}
                {committed.merged ? ` · merged ${committed.merged} into existing holdings` : ''}
                {committed.skipped ? ` · skipped ${committed.skipped}` : ''}.
                {committed.activity_inserted > 0 && (
                  <div className="mt-1">
                    Also imported {committed.activity_inserted} activity row{committed.activity_inserted === 1 ? '' : 's'} as transactions.
                  </div>
                )}
              </div>
              {(committed.errors?.length > 0 || committed.activity_errors?.length > 0) && (
                <div className="mt-3 text-xs text-amber-700 dark:text-amber-400">
                  {(committed.errors?.length || 0)} holding{(committed.errors?.length || 0) === 1 ? '' : 's'}
                  {committed.activity_errors?.length > 0 && ` · ${committed.activity_errors.length} activity issue${committed.activity_errors.length === 1 ? '' : 's'}`}
                  {' '}could not be saved.
                </div>
              )}
            </div>
          )}

          {/* ---- FOOTER ----------------------------------------------------- */}
          <DialogFooter className="border-t border-border/60 pt-3 mt-0 flex-row gap-2 justify-between sm:justify-between">
            {phase === 'review' ? (
              <>
                <div className="text-sm text-muted-foreground self-center">
                  {selectedCount} of {items.length} selected
                  {mergeCount > 0 && <span className="ml-2 text-emerald-700 dark:text-emerald-400">· {mergeCount} will merge</span>}
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => handleOpenChange(false)} data-testid="inv-import-cancel-button">
                    Cancel
                  </Button>
                  <Button
                    onClick={commit}
                    disabled={selectedCount === 0 && !(importActivity && csvActivity.length > 0)}
                    className="rounded-xl"
                    data-testid="inv-import-commit-button"
                  >
                    {(() => {
                      const bits = [];
                      if (selectedCount > 0) bits.push(`${selectedCount} holding${selectedCount === 1 ? '' : 's'}`);
                      if (importActivity && csvActivity.length > 0) bits.push(`${csvActivity.length} activity`);
                      return bits.length > 0 ? `Import ${bits.join(' + ')}` : 'Import';
                    })()}
                  </Button>
                </div>
              </>
            ) : (
              <Button variant="ghost" onClick={() => handleOpenChange(false)} data-testid="inv-import-close-button">
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Locked-PDF password dialog. Lives outside the main dialog so it
          stacks correctly above it. */}
      <LockedPdfDialog
        open={!!lockedPdf}
        onOpenChange={(v) => { if (!v) setLockedPdf(null); }}
        filename={lockedPdf?.file?.name || ''}
        errorMessage={lockedPdf?.errorMessage || null}
        busy={lockedPdf?.busy || false}
        onSubmit={submitLockedPdfPassword}
        onCancel={() => { setLockedPdf(null); setFile(null); setPhase('idle'); }}
      />
    </>
  );
}
